import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/Button'
import { useUploadEmployeePicture } from '@/hooks/useWorkforceMutations'
import { Camera, Upload, X, Loader2 } from 'lucide-react'

interface EmployeePictureUploadProps {
  employeeId: string
  currentPictureUrl?: string
  employeeName: string
}

export function EmployeePictureUpload({
  employeeId,
  currentPictureUrl,
  employeeName
}: EmployeePictureUploadProps) {
  const [preview, setPreview] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMutation = useUploadEmployeePicture()

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB')
      return
    }

    setSelectedFile(file)

    // Generate preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      await uploadMutation.mutateAsync({
        employeeId,
        file: selectedFile
      })

      // Reset state after successful upload
      setSelectedFile(null)
      setPreview(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  const handleCancel = () => {
    setSelectedFile(null)
    setPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const nameParts = employeeName.split(' ').filter(p => p.length > 0)
  const initials = nameParts.length >= 2 ?
    `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}` :
    nameParts.length === 1 ?
    `${nameParts[0][0]}${nameParts[0][1] || 'N'}` :
    'NA'

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Picture Display */}
      <div className="relative group">
        {preview || currentPictureUrl ? (
          <img
            src={preview || currentPictureUrl}
            alt={employeeName}
            className="w-32 h-32 rounded-full object-cover border-4 border-brand-border"
          />
        ) : (
          <div className="w-32 h-32 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center border-4 border-brand-border">
            <span className="text-3xl font-bold text-brand-dark">
              {initials.toUpperCase()}
            </span>
          </div>
        )}

        {/* Upload Button Overlay */}
        {!preview && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Camera className="w-8 h-8 text-white" />
          </button>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload Actions */}
      {selectedFile && (
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            onClick={handleUpload}
            disabled={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Enviar Foto
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={uploadMutation.isPending}
          >
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
        </div>
      )}

      {/* Upload Button (when no file selected) */}
      {!selectedFile && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          <Camera className="w-4 h-4 mr-2" />
          Alterar Foto
        </Button>
      )}

      <p className="text-xs text-brand-gray text-center">
        Formatos aceitos: JPG, PNG, GIF (máx. 5MB)
      </p>
    </div>
  )
}
